import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import {
    Play, Square, Plus, Minus, Trophy, Clock, Users, Target,
    Zap, AlertCircle, CheckCircle2, RefreshCw, Timer, Flag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport-specific scoring configurations
const SPORT_CONFIGS = {
    'Basketball': {
        points: [1, 2, 3],
        pointLabels: ['+1 FT', '+2', '+3'],
        periods: ['Q1', 'Q2', 'Q3', 'Q4', 'OT'],
        eventTypes: ['basket', 'free_throw', 'three_pointer', 'foul', 'timeout'],
        scoringType: 'cumulative' // Points add up
    },
    'Football': {
        points: [1],
        pointLabels: ['Goal'],
        periods: ['1st Half', '2nd Half', 'Extra Time'],
        eventTypes: ['goal', 'own_goal', 'penalty', 'foul', 'yellow_card', 'red_card', 'substitution'],
        scoringType: 'cumulative'
    },
    'Rugby': {
        points: [3, 5, 7, 2],
        pointLabels: ['Penalty/Drop', 'Try', 'Conv Try', 'Conversion'],
        periods: ['1st Half', '2nd Half'],
        eventTypes: ['try', 'conversion', 'penalty', 'drop_goal', 'yellow_card', 'red_card'],
        scoringType: 'cumulative'
    },
    'Volleyball': {
        points: [1],
        pointLabels: ['Point'],
        periods: ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5'],
        eventTypes: ['point', 'ace', 'block', 'timeout'],
        scoringType: 'cumulative' // Per set
    },
    'Hockey': {
        points: [1],
        pointLabels: ['Goal'],
        periods: ['Q1', 'Q2', 'Q3', 'Q4'],
        eventTypes: ['goal', 'penalty_corner', 'green_card', 'yellow_card', 'red_card'],
        scoringType: 'cumulative'
    },
    'Tennis': {
        points: ['0', '15', '30', '40', 'AD'],
        pointLabels: ['Point Won'],
        periods: ['Set 1', 'Set 2', 'Set 3', 'Set 4', 'Set 5'],
        eventTypes: ['game', 'ace', 'double_fault', 'break_point'],
        scoringType: 'tennis', // Special tennis scoring
        gameScoreSequence: ['0', '15', '30', '40', 'Game'],
        tiebreakMode: false
    },
    'Table Tennis': {
        points: [1],
        pointLabels: ['Point'],
        periods: ['Game 1', 'Game 2', 'Game 3', 'Game 4', 'Game 5', 'Game 6', 'Game 7'],
        eventTypes: ['point', 'timeout', 'let'],
        scoringType: 'cumulative' // Per game, first to 11 (win by 2)
    },
    'Badminton': {
        points: [1],
        pointLabels: ['Point'],
        periods: ['Game 1', 'Game 2', 'Game 3'],
        eventTypes: ['point', 'fault', 'let'],
        scoringType: 'cumulative' // Per game, first to 21 (win by 2)
    },
    'Squash': {
        points: [1],
        pointLabels: ['Point'],
        periods: ['Game 1', 'Game 2', 'Game 3', 'Game 4', 'Game 5'],
        eventTypes: ['point', 'let', 'stroke'],
        scoringType: 'cumulative' // Per game, first to 11 (win by 2)
    },
    'Handball': {
        points: [1],
        pointLabels: ['Goal'],
        periods: ['1st Half', '2nd Half', 'Extra Time'],
        eventTypes: ['goal', 'penalty', 'yellow_card', 'red_card', 'timeout'],
        scoringType: 'cumulative'
    },
    'Netball': {
        points: [1, 2],
        pointLabels: ['Goal', 'Super Shot'],
        periods: ['Q1', 'Q2', 'Q3', 'Q4'],
        eventTypes: ['goal', 'super_shot', 'penalty'],
        scoringType: 'cumulative'
    },
    'Cricket': {
        points: [1, 2, 3, 4, 6],
        pointLabels: ['1 Run', '2 Runs', '3 Runs', '4 (Boundary)', '6 (Six)'],
        periods: ['1st Innings', '2nd Innings'],
        eventTypes: ['runs', 'wicket', 'wide', 'no_ball', 'bye', 'leg_bye'],
        scoringType: 'cumulative'
    },
    'Swimming': {
        points: [],
        pointLabels: [],
        periods: ['Heat', 'Semi-Final', 'Final'],
        eventTypes: ['finish', 'false_start', 'disqualification'],
        scoringType: 'time' // Time-based, not points
    },
    'Athletics Track and Field': {
        points: [],
        pointLabels: [],
        periods: ['Heat', 'Semi-Final', 'Final'],
        eventTypes: ['finish', 'false_start', 'disqualification', 'foul'],
        scoringType: 'time' // Time or distance based
    },
    'default': {
        points: [1, 2, 3],
        pointLabels: ['+1', '+2', '+3'],
        periods: ['1st Half', '2nd Half', 'Extra Time'],
        eventTypes: ['score', 'foul', 'timeout'],
        scoringType: 'cumulative'
    }
};

const LiveScoringTab = ({ tournaments, token }) => {
    const [loading, setLoading] = useState(true);
    const [matches, setMatches] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('all');
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [showScoringPanel, setShowScoringPanel] = useState(false);
    const [matchEvents, setMatchEvents] = useState([]);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);

    // Create a stable axios instance using useMemo
    const api = useMemo(() => axios.create({
        baseURL: `${API_URL}/api`,
        headers: { Authorization: `Bearer ${token}` }
    }), [token]);

    const loadMatches = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            let allMatches = [];
            const tournamentsToLoad = selectedTournament === 'all' 
                ? tournaments 
                : tournaments.filter(t => t.id === selectedTournament);

            for (const t of tournamentsToLoad || []) {
                try {
                    const res = await api.get(`/matches/tournament/${t.id}/matches`);
                    const tournamentMatches = (res.data || []).map(m => ({
                        ...m,
                        tournament_name: t.name,
                        sport: t.sport
                    }));
                    allMatches = [...allMatches, ...tournamentMatches];
                } catch (err) {
                    // Tournament might not have matches
                }
            }

            // Sort: live matches first, then by scheduled time
            allMatches.sort((a, b) => {
                if (a.status === 'live' && b.status !== 'live') return -1;
                if (a.status !== 'live' && b.status === 'live') return 1;
                return new Date(a.scheduled_date || a.date) - new Date(b.scheduled_date || b.date);
            });

            setMatches(allMatches);
        } catch (error) {
            console.error('Error loading matches:', error);
        } finally {
            setLoading(false);
            setInitialLoadDone(true);
        }
    }, [selectedTournament, tournaments, api]);

    // Initial load and when tournament filter changes
    useEffect(() => {
        loadMatches(true);
    }, [selectedTournament, tournaments, token]);

    // Auto-refresh for live matches (silent refresh, no loading indicator)
    useEffect(() => {
        if (!autoRefresh || !initialLoadDone) return;
        const interval = setInterval(() => loadMatches(false), 10000);
        return () => clearInterval(interval);
    }, [autoRefresh, loadMatches, initialLoadDone]);

    const startMatch = async (match) => {
        try {
            await api.post(`/live-scoring/match/${match.id}/start`);
            toast.success('Match started!');
            loadMatches(false);
        } catch (error) {
            toast.error('Failed to start match');
        }
    };

    const endMatch = async (match) => {
        try {
            await api.post(`/live-scoring/match/${match.id}/end`);
            toast.success('Match ended!');
            loadMatches(false);
            setShowScoringPanel(false);
        } catch (error) {
            toast.error('Failed to end match');
        }
    };

    const openScoringPanel = async (match) => {
        // Fetch live state to rehydrate persisted racquet_state from the DB
        try {
            const stateRes = await api.get(`/live-scoring/match/${match.id}/state`);
            if (stateRes.data?.racquet_state) {
                match = { ...match, racquet_state: stateRes.data.racquet_state };
            }
        } catch (e) {}
        
        setSelectedMatch(match);
        setShowScoringPanel(true);
        
        // Load match events
        try {
            const res = await api.get(`/live-scoring/match/${match.id}/events`);
            setMatchEvents(res.data || []);
        } catch (error) {
            setMatchEvents([]);
        }
    };

    const updateScore = async (team, points, eventType = 'score', playerName = null, racquetState = null) => {
        if (!selectedMatch) return;
        
        try {
            const payload = {
                team,
                points,
                event_type: eventType,
                player_name: playerName,
                period: selectedMatch.current_period
            };
            if (racquetState) payload.racquet_state = racquetState;

            const res = await api.post(`/live-scoring/match/${selectedMatch.id}/score`, payload);
            
            // Update local state
            setSelectedMatch(prev => ({
                ...prev,
                score1: res.data.home_score,
                score2: res.data.away_score,
                ...(racquetState ? { racquet_state: racquetState } : {})
            }));
            
            // Refresh events
            const eventsRes = await api.get(`/live-scoring/match/${selectedMatch.id}/events`);
            setMatchEvents(eventsRes.data || []);
            
            loadMatches(false);
        } catch (error) {
            toast.error('Failed to update score');
        }
    };

    const updatePeriod = async (period) => {
        if (!selectedMatch) return;
        
        try {
            await api.put(`/live-scoring/match/${selectedMatch.id}/period?period=${period}`);
            setSelectedMatch(prev => ({ ...prev, current_period: period }));
            toast.success(`Period changed to ${period}`);
        } catch (error) {
            toast.error('Failed to update period');
        }
    };

    const deleteEvent = async (eventId) => {
        if (!selectedMatch) return;
        
        try {
            await api.delete(`/live-scoring/match/${selectedMatch.id}/event/${eventId}`);
            
            // Refresh events and match
            const eventsRes = await api.get(`/live-scoring/match/${selectedMatch.id}/events`);
            setMatchEvents(eventsRes.data || []);
            
            const stateRes = await api.get(`/live-scoring/match/${selectedMatch.id}/state`);
            setSelectedMatch(prev => ({
                ...prev,
                score1: stateRes.data.home_score,
                score2: stateRes.data.away_score
            }));
            
            loadMatches(false);
            toast.success('Event undone');
        } catch (error) {
            toast.error('Failed to undo event');
        }
    };

    const getSportConfig = (sport) => {
        return SPORT_CONFIGS[sport] || SPORT_CONFIGS.default;
    };

    const liveMatches = matches.filter(m => m.status === 'live');
    const scheduledMatches = matches.filter(m => m.status === 'scheduled');

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-heading font-bold uppercase tracking-tight">
                        Live Scoring Control
                    </h2>
                    <p className="text-sm text-muted-foreground">
                        Update match scores in real-time
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Button
                        variant={autoRefresh ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAutoRefresh(!autoRefresh)}
                        className="border-white/10"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                        {autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh'}
                    </Button>
                    <Select value={selectedTournament} onValueChange={setSelectedTournament}>
                        <SelectTrigger className="w-[200px] bg-[#0a1628] border-white/10">
                            <SelectValue placeholder="Select Tournament" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10">
                            <SelectItem value="all">All Tournaments</SelectItem>
                            {tournaments?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Live Matches Section */}
            {liveMatches.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                        <h3 className="text-lg font-bold text-red-400">LIVE NOW ({liveMatches.length})</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {liveMatches.map(match => (
                            <LiveMatchCard
                                key={match.id}
                                match={match}
                                onOpenScoring={() => openScoringPanel(match)}
                                onEndMatch={() => endMatch(match)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Scheduled Matches */}
            <div className="space-y-3">
                <h3 className="text-lg font-bold">Scheduled Matches ({scheduledMatches.length})</h3>
                {!initialLoadDone ? (
                    <div className="text-center py-8 text-muted-foreground">Loading matches...</div>
                ) : scheduledMatches.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No scheduled matches</div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {scheduledMatches.map(match => (
                            <ScheduledMatchCard
                                key={match.id}
                                match={match}
                                onStart={() => startMatch(match)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Scoring Panel Dialog */}
            <Dialog open={showScoringPanel} onOpenChange={setShowScoringPanel}>
                <DialogContent className="max-w-4xl bg-[#0a1628] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                            LIVE SCORING: {selectedMatch?.tournament_name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedMatch && (
                        <ScoringPanel
                            match={selectedMatch}
                            events={matchEvents}
                            sportConfig={getSportConfig(selectedMatch.sport)}
                            onUpdateScore={updateScore}
                            onUpdatePeriod={updatePeriod}
                            onDeleteEvent={deleteEvent}
                            onEndMatch={() => endMatch(selectedMatch)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Live Match Card Component
const LiveMatchCard = ({ match, onOpenScoring, onEndMatch }) => (
    <div className="bg-gradient-to-br from-red-500/20 to-orange-500/10 border border-red-500/30 p-4 relative">
        <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-500 text-white text-xs px-2 py-1">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
        </div>
        
        <div className="text-xs text-muted-foreground mb-2">{match.tournament_name}</div>
        <div className="text-xs text-cyan-400 mb-3">{match.sport} • {match.round}</div>
        
        <div className="flex items-center justify-between mb-4">
            <div className="text-center flex-1">
                <div className="font-bold text-lg">{match.team1 || 'TBD'}</div>
                <div className="text-4xl font-bold text-cyan-400">{match.score1 || 0}</div>
            </div>
            <div className="text-2xl font-bold text-muted-foreground mx-4">VS</div>
            <div className="text-center flex-1">
                <div className="font-bold text-lg">{match.team2 || 'TBD'}</div>
                <div className="text-4xl font-bold text-cyan-400">{match.score2 || 0}</div>
            </div>
        </div>
        
        <div className="text-center text-sm text-muted-foreground mb-3">
            {match.current_period || 'In Progress'}
        </div>
        
        <div className="flex gap-2">
            <Button 
                onClick={onOpenScoring}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black"
            >
                <Target className="w-4 h-4 mr-2" />
                Update Score
            </Button>
            <Button 
                onClick={onEndMatch}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
                <Square className="w-4 h-4" />
            </Button>
        </div>
    </div>
);

// Scheduled Match Card
const ScheduledMatchCard = ({ match, onStart }) => (
    <div className="bg-[#0d2847] border border-white/10 p-4">
        <div className="text-xs text-muted-foreground mb-1">{match.tournament_name}</div>
        <div className="text-xs text-cyan-400 mb-3">{match.sport} • {match.round}</div>
        
        <div className="flex items-center justify-between mb-3">
            <div className="font-medium">{match.team1 || 'TBD'}</div>
            <div className="text-sm text-muted-foreground">vs</div>
            <div className="font-medium">{match.team2 || 'TBD'}</div>
        </div>
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Clock className="w-4 h-4" />
            {match.scheduled_date} {match.scheduled_time}
        </div>
        
        <Button 
            onClick={onStart}
            className="w-full bg-green-500 hover:bg-green-400 text-black"
            disabled={!match.team1 || !match.team2}
        >
            <Play className="w-4 h-4 mr-2" />
            Start Match
        </Button>
    </div>
);

// Scoring Panel Component
const ScoringPanel = ({ match, events, sportConfig, onUpdateScore, onUpdatePeriod, onDeleteEvent, onEndMatch }) => {
    const [selectedPlayer, setSelectedPlayer] = useState('');
    const [tennisScore, setTennisScore] = useState(() => ({
        home: { 
            sets: match?.racquet_state?.home_sets ?? 0, 
            games: match?.racquet_state?.home_games ?? 0, 
            points: match?.racquet_state?.home_points ?? '0' 
        },
        away: { 
            sets: match?.racquet_state?.away_sets ?? 0, 
            games: match?.racquet_state?.away_games ?? 0, 
            points: match?.racquet_state?.away_points ?? '0' 
        }
    }));

    // Re-sync when a different match is loaded with persisted racquet state
    React.useEffect(() => {
        if (match?.racquet_state) {
            setTennisScore({
                home: {
                    sets: match.racquet_state.home_sets ?? 0,
                    games: match.racquet_state.home_games ?? 0,
                    points: match.racquet_state.home_points ?? '0'
                },
                away: {
                    sets: match.racquet_state.away_sets ?? 0,
                    games: match.racquet_state.away_games ?? 0,
                    points: match.racquet_state.away_points ?? '0'
                }
            });
        }
    }, [match?.id, match?.racquet_state]);
    
    // Tennis scoring logic
    const TENNIS_POINTS = ['0', '15', '30', '40'];
    
    const handleTennisPoint = (team) => {
        const newScore = { ...tennisScore };
        const opponent = team === 'home' ? 'away' : 'home';
        
        const currentIdx = TENNIS_POINTS.indexOf(newScore[team].points);
        const opponentIdx = TENNIS_POINTS.indexOf(newScore[opponent].points);
        
        // Deuce situations
        if (newScore[team].points === '40' && newScore[opponent].points === '40') {
            newScore[team].points = 'AD';
        } else if (newScore[team].points === 'AD') {
            // Win the game
            newScore[team].games += 1;
            newScore.home.points = '0';
            newScore.away.points = '0';
            // Check for set win (6 games, lead by 2)
            if (newScore[team].games >= 6 && newScore[team].games - newScore[opponent].games >= 2) {
                newScore[team].sets += 1;
                newScore.home.games = 0;
                newScore.away.games = 0;
            }
        } else if (newScore[opponent].points === 'AD') {
            // Back to deuce
            newScore[opponent].points = '40';
        } else if (newScore[team].points === '40') {
            // Win the game
            newScore[team].games += 1;
            newScore.home.points = '0';
            newScore.away.points = '0';
            // Check for set win
            if (newScore[team].games >= 6 && newScore[team].games - newScore[opponent].games >= 2) {
                newScore[team].sets += 1;
                newScore.home.games = 0;
                newScore.away.games = 0;
            }
        } else {
            // Normal point progression
            newScore[team].points = TENNIS_POINTS[currentIdx + 1];
        }
        
        setTennisScore(newScore);
        
        // Persist granular racquet_state on the backend so it survives page refresh
        const racquetState = {
            home_sets: newScore.home.sets,
            home_games: newScore.home.games,
            home_points: newScore.home.points,
            away_sets: newScore.away.sets,
            away_games: newScore.away.games,
            away_points: newScore.away.points,
            current_set: Math.max(newScore.home.sets, newScore.away.sets) + 1
        };
        onUpdateScore(team, 0, 'tennis_point', selectedPlayer || null, racquetState);
    };
    
    const isTennis = sportConfig.scoringType === 'tennis';
    const isTimeBased = sportConfig.scoringType === 'time';
    
    return (
        <div className="space-y-6">
            {/* Score Display */}
            <div className="bg-[#0d2847] p-6 text-center">
                {isTennis ? (
                    // Tennis Score Display
                    <div className="space-y-4">
                        <div className="grid grid-cols-5 gap-2 text-center text-sm font-medium text-muted-foreground">
                            <div></div>
                            <div>Sets</div>
                            <div>Games</div>
                            <div>Points</div>
                            <div></div>
                        </div>
                        <div className="grid grid-cols-5 gap-2 items-center">
                            <div className="text-lg font-bold text-left">{match.team1}</div>
                            <div className="text-4xl font-bold text-cyan-400">{tennisScore.home.sets}</div>
                            <div className="text-3xl font-bold text-yellow-400">{tennisScore.home.games}</div>
                            <div className="text-2xl font-bold text-white bg-green-600 rounded px-3 py-1">{tennisScore.home.points}</div>
                            <Button
                                onClick={() => handleTennisPoint('home')}
                                className="bg-green-500 hover:bg-green-400 text-black font-bold"
                            >
                                Point
                            </Button>
                        </div>
                        <div className="grid grid-cols-5 gap-2 items-center">
                            <div className="text-lg font-bold text-left">{match.team2}</div>
                            <div className="text-4xl font-bold text-cyan-400">{tennisScore.away.sets}</div>
                            <div className="text-3xl font-bold text-yellow-400">{tennisScore.away.games}</div>
                            <div className="text-2xl font-bold text-white bg-blue-600 rounded px-3 py-1">{tennisScore.away.points}</div>
                            <Button
                                onClick={() => handleTennisPoint('away')}
                                className="bg-blue-500 hover:bg-blue-400 text-white font-bold"
                            >
                                Point
                            </Button>
                        </div>
                        <div className="text-sm text-muted-foreground">
                            Current Set: {match.current_period || 'Set 1'}
                        </div>
                    </div>
                ) : isTimeBased ? (
                    // Time-based sports (Swimming, Athletics)
                    <div className="space-y-4">
                        <div className="text-2xl font-bold mb-4">Time-Based Event</div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 bg-[#0a1628] rounded">
                                <div className="text-lg font-bold mb-2">{match.team1}</div>
                                <Input
                                    type="text"
                                    placeholder="Time (e.g., 10.23)"
                                    className="bg-[#0d2847] border-white/10 text-center text-2xl"
                                />
                            </div>
                            <div className="p-4 bg-[#0a1628] rounded">
                                <div className="text-lg font-bold mb-2">{match.team2}</div>
                                <Input
                                    type="text"
                                    placeholder="Time (e.g., 10.45)"
                                    className="bg-[#0d2847] border-white/10 text-center text-2xl"
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    // Standard cumulative scoring
                    <div className="flex items-center justify-center gap-8">
                        <div className="flex-1">
                            <div className="text-xl font-bold mb-2">{match.team1}</div>
                            <div className="text-6xl font-bold text-cyan-400">{match.score1 || 0}</div>
                        </div>
                        <div className="text-center">
                            <div className="text-sm text-muted-foreground mb-2">Period</div>
                            <Select value={match.current_period || ''} onValueChange={onUpdatePeriod}>
                                <SelectTrigger className="w-32 bg-[#0a1628] border-white/10">
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0d2847] border-white/10">
                                    {sportConfig.periods.map(p => (
                                        <SelectItem key={p} value={p}>{p}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex-1">
                            <div className="text-xl font-bold mb-2">{match.team2}</div>
                            <div className="text-6xl font-bold text-cyan-400">{match.score2 || 0}</div>
                        </div>
                    </div>
                )}
            </div>

            {/* Quick Score Buttons - Only for cumulative sports */}
            {!isTennis && !isTimeBased && sportConfig.points.length > 0 && (
                <div className="grid grid-cols-2 gap-6">
                    {/* Home Team */}
                    <div className="space-y-3">
                        <div className="text-center font-bold text-lg">{match.team1}</div>
                        <Input
                            placeholder="Player name (optional)"
                            value={selectedPlayer}
                            onChange={(e) => setSelectedPlayer(e.target.value)}
                            className="bg-[#0a1628] border-white/10"
                        />
                        <div className="flex gap-2 flex-wrap justify-center">
                            {sportConfig.points.map((pts, idx) => (
                                <Button
                                    key={pts}
                                    onClick={() => {
                                        onUpdateScore('home', pts, 'score', selectedPlayer || null);
                                        setSelectedPlayer('');
                                    }}
                                    className="bg-green-500 hover:bg-green-400 text-black text-sm font-bold min-w-16 h-14 px-2"
                                    title={sportConfig.pointLabels?.[idx] || `+${pts}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <span className="text-lg">+{pts}</span>
                                        {sportConfig.pointLabels?.[idx] && (
                                            <span className="text-xs opacity-80">{sportConfig.pointLabels[idx]}</span>
                                        )}
                                    </div>
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Away Team */}
                    <div className="space-y-3">
                        <div className="text-center font-bold text-lg">{match.team2}</div>
                        <Input
                            placeholder="Player name (optional)"
                            className="bg-[#0a1628] border-white/10"
                        />
                        <div className="flex gap-2 flex-wrap justify-center">
                            {sportConfig.points.map((pts, idx) => (
                                <Button
                                    key={pts}
                                    onClick={() => onUpdateScore('away', pts, 'score')}
                                    className="bg-blue-500 hover:bg-blue-400 text-white text-sm font-bold min-w-16 h-14 px-2"
                                    title={sportConfig.pointLabels?.[idx] || `+${pts}`}
                                >
                                    <div className="flex flex-col items-center">
                                        <span className="text-lg">+{pts}</span>
                                        {sportConfig.pointLabels?.[idx] && (
                                            <span className="text-xs opacity-80">{sportConfig.pointLabels[idx]}</span>
                                        )}
                                    </div>
                                </Button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Tennis-specific buttons */}
            {isTennis && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                        <div className="text-center font-bold">{match.team1}</div>
                        <div className="grid grid-cols-2 gap-2">
                            <Button size="sm" variant="outline" className="border-green-500 text-green-400">
                                Ace
                            </Button>
                            <Button size="sm" variant="outline" className="border-yellow-500 text-yellow-400">
                                Winner
                            </Button>
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="text-center font-bold">{match.team2}</div>
                        <div className="grid grid-cols-2 gap-2">
                            <Button size="sm" variant="outline" className="border-blue-500 text-blue-400">
                                Ace
                            </Button>
                            <Button size="sm" variant="outline" className="border-yellow-500 text-yellow-400">
                                Winner
                            </Button>
                        </div>
                    </div>
                    <div className="col-span-2 grid grid-cols-2 gap-2">
                        <Button size="sm" variant="outline" className="border-red-500 text-red-400">
                            Double Fault ({match.team1})
                        </Button>
                        <Button size="sm" variant="outline" className="border-red-500 text-red-400">
                            Double Fault ({match.team2})
                        </Button>
                    </div>
                </div>
            )}

            {/* Recent Events */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <h4 className="font-bold">Recent Events</h4>
                    <span className="text-xs text-muted-foreground">Click to undo</span>
                </div>
                <div className="max-h-40 overflow-y-auto space-y-2">
                    {events.slice(0, 10).map(event => (
                        <div 
                            key={event.id}
                            onClick={() => onDeleteEvent(event.id)}
                            className="flex items-center justify-between p-2 bg-[#0d2847] border border-white/10 cursor-pointer hover:border-red-500/50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${event.team === 'home' ? 'bg-green-500' : 'bg-blue-500'}`} />
                                <span className="text-sm">{event.description || `+${event.points} ${event.team_name || event.team}`}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{event.period}</span>
                        </div>
                    ))}
                    {events.length === 0 && (
                        <div className="text-center text-sm text-muted-foreground py-4">
                            No events yet
                        </div>
                    )}
                </div>
            </div>

            {/* End Match Button */}
            <Button 
                onClick={onEndMatch}
                variant="outline"
                className="w-full border-red-500/50 text-red-400 hover:bg-red-500/20"
            >
                <Flag className="w-4 h-4 mr-2" />
                End Match
            </Button>
        </div>
    );
};

export default LiveScoringTab;
